import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Computer as ComputerIcon,
  AttachMoney as AttachMoneyIcon,
  Public as PublicIcon,
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  BusinessCenter as BusinessCenterIcon,
  FormatListBulleted as FormatListBulletedIcon,
  ContactMail as ContactMailIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import projectService from '../../services/project.service';
import { Project, projectStatusColors } from '../../types/Project';

const ProjectDetails: React.FC = () => {
  const { id: companyId, projectId } = useParams<{ id: string; projectId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // State
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      if (!companyId || !projectId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await projectService.getProject(companyId, projectId);
        setProject(data);
        
        // Increment view count (non-blocking)
        projectService.incrementViewCount(companyId, projectId);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to fetch project details');
        console.error('Error fetching project:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [companyId, projectId]);
  
  // Delete project handler
  const handleDeleteProject = async () => {
    if (!companyId || !projectId) return;
    
    try {
      await projectService.deleteProject(companyId, projectId);
      navigate(`/companies/${companyId}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
      setDeleteDialogOpen(false);
    }
  };
  
  // Format experience
  const formatExperience = (min?: number, max?: number) => {
    if (min === undefined || max === undefined) return 'Not specified';
    
    if (min === max) {
      return `${min} years`;
    }
    
    return `${min}-${max} years`;
  };
  
  // Format work mode icon
  const getWorkModeIcon = (workMode: string) => {
    switch (workMode) {
      case 'remote':
        return <ComputerIcon fontSize="small" />;
      case 'hybrid':
        return <PublicIcon fontSize="small" />;
      default:
        return <LocationOnIcon fontSize="small" />;
    }
  };
  
  // Format job type
  const formatJobType = (jobType: string) => {
    return jobType.replace('_', ' ');
  };
  
  // Format salary
  const formatSalary = (salary: any) => {
    if (!salary) return 'Not specified';
    
    if (!salary.min && !salary.max) return 'Competitive';
    
    let base = '';
    if (salary.min && salary.max) {
      base = `${salary.currency}${salary.min.toLocaleString()}-${salary.currency}${salary.max.toLocaleString()}`;
    } else if (salary.min) {
      base = `${salary.currency}${salary.min.toLocaleString()}+`;
    } else if (salary.max) {
      base = `Up to ${salary.currency}${salary.max.toLocaleString()}`;
    }
    
    return `${base} per ${salary.period}`;
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}`)}
        >
          Back to Company
        </Button>
      </Box>
    );
  }
  
  if (!project) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Project not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}`)}
        >
          Back to Company
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header with back button and actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(`/companies/${companyId}`)} 
          sx={{ mr: 1 }}
          aria-label="Back to company details"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Project Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      {/* Project Header */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            bgcolor: projectStatusColors[project.status],
          }
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: projectStatusColors[project.status],
                  mr: 1,
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  textTransform: 'uppercase',
                  fontWeight: 'medium',
                  color: projectStatusColors[project.status],
                }}
              >
                {project.status}
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
              {project.project_name}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={formatJobType(project.job_type)}
                size="small"
                variant="outlined"
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'capitalize',
                }}
              />
              <Chip
                label={project.work_mode.replace('_', ' ')}
                size="small"
                variant="outlined"
                icon={getWorkModeIcon(project.work_mode)}
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'capitalize',
                }}
              />
              {project.location && (
                <Chip
                  label={project.location}
                  size="small"
                  variant="outlined"
                  icon={<LocationOnIcon fontSize="small" />}
                  sx={{ borderRadius: 1 }}
                />
              )}
            </Box>
            
            <Typography variant="body1" color="text.secondary">
              {project.description}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Experience:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatExperience(project.experience?.min, project.experience?.max)}
                </Typography>
              </Box>
              
              {project.salary && project.salary.is_displayed && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Salary:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatSalary(project.salary)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Project Details */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          {/* Skills */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Required Skills
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {project.must_have_skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    sx={{
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      mb: 0.5,
                    }}
                  />
                ))}
                {project.must_have_skills.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No required skills specified
                  </Typography>
                )}
              </Box>
              
              {project.good_to_have_skills && project.good_to_have_skills.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                    Good to Have Skills
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {project.good_to_have_skills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Key Responsibilities */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Key Responsibilities
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List dense disablePadding>
                {project.key_responsibilities.map((responsibility, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
                    <ListItemText primary={responsibility} />
                  </ListItem>
                ))}
                {project.key_responsibilities.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No responsibilities specified
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Qualifications */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessCenterIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Qualifications
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List dense disablePadding>
                {project.qualifications.map((qualification, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
                    <ListItemText primary={qualification} />
                  </ListItem>
                ))}
                {project.qualifications.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No qualifications specified
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Benefits */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormatListBulletedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Why Join Us?
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List dense disablePadding>
                {project.why_join_us.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
                {project.why_join_us.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No benefits specified
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column */}
        <Grid item xs={12} md={6}>
          {/* Work Details */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Work Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                {/* Salary */}
                {project.salary && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <AttachMoneyIcon />
                    </Box>
                    <ListItemText 
                      primary="Salary"
                      secondary={
                        <>
                          <Typography variant="body2">
                            {formatSalary(project.salary)}
                          </Typography>
                          {!project.salary.is_displayed && (
                            <Typography variant="caption" color="text.secondary">
                              (Not displayed to applicants)
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                )}
                
                {/* Work Schedule */}
                {project.work_schedule && (
                  <>
                    {(project.work_schedule.start_time || project.work_schedule.end_time) && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          <AccessTimeIcon />
                        </Box>
                        <ListItemText 
                          primary="Working Hours"
                          secondary={
                            <>
                              {project.work_schedule.start_time && project.work_schedule.end_time ? (
                                `${project.work_schedule.start_time} to ${project.work_schedule.end_time}`
                              ) : (
                                project.work_schedule.start_time || project.work_schedule.end_time || 'Flexible'
                              )}
                              {project.work_schedule.timezone && ` (${project.work_schedule.timezone})`}
                            </>
                          }
                        />
                      </ListItem>
                    )}
                    
                    {project.work_schedule.days && project.work_schedule.days.length > 0 && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          <CalendarTodayIcon />
                        </Box>
                        <ListItemText 
                          primary="Working Days"
                          secondary={
                            project.work_schedule.days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')
                          }
                        />
                      </ListItem>
                    )}
                  </>
                )}
                
                {/* Notice Period */}
                {project.notice_period && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <TimerIcon />
                    </Box>
                    <ListItemText 
                      primary="Notice Period"
                      secondary={project.notice_period}
                    />
                  </ListItem>
                )}
                
                {/* Screening Interview Type */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ mr: 2, color: 'primary.main' }}>
                    <ContactMailIcon />
                  </Box>
                  <ListItemText 
                    primary="Screening Interview"
                    secondary={
                      project.screening_interview_type === 'on_call' ? 'On Call' :
                      project.screening_interview_type === 'on_website' ? 'On Website' : 
                      'Both (On Call and Website)'
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          
          {/* Application Form Details */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Application Form
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem sx={{ px: 0, py: 0.75 }}>
                  <ListItemText 
                    primary="Resume Required"
                    secondary={project.application_form_fields?.resume?.required ? 'Yes' : 'No'}
                  />
                </ListItem>
                
                <ListItem sx={{ px: 0, py: 0.75 }}>
                  <ListItemText 
                    primary="Cover Letter Required"
                    secondary={project.application_form_fields?.cover_letter?.required ? 'Yes' : 'No'}
                  />
                </ListItem>
                
                {project.application_form_fields?.custom_questions && project.application_form_fields.custom_questions.length > 0 && (
                  <ListItem sx={{ px: 0, py: 0.75 }}>
                    <ListItemText 
                      primary={`Custom Questions (${project.application_form_fields.custom_questions.length})`}
                      secondary={
                        <List dense>
                          {project.application_form_fields.custom_questions.map((q, i) => (
                            <ListItem key={i} sx={{ px: 0, py: 0.25 }}>
                              <Typography variant="body2">
                                {i+1}. {q.question} {q.required ? '(Required)' : '(Optional)'}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Other Requirements */}
          {project.other_requirements && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HelpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Other Requirements
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2">
                  {project.other_requirements}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
      
      {/* Delete Project Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this project? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetails;